import { useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import Paths from "@/constants/paths";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { FinalizeApi } from "@/types/finalizeApi.type";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { useFinalizeApiOptions } from "@/hooks/useEnumOptions";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

export const FinalizeExpeditionModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const finalizeApiOptions = useFinalizeApiOptions();
  const navigate = useNavigate();
  const id = useId();
  const [selected, setSelected] = useState<FinalizeApi | undefined>(undefined);

  const handleNavigate = (apiEnum: FinalizeApi) => {
    onClose();
    navigate(Paths.bo.expeditionForm.replace(":id", expedition.id), { state: { apiEnum } });
  };

  const onConfirm = () => {
    if (!selected) return;
    handleNavigate(selected);
  };

  const finalizeOptions = useMemo(() => {
    if (expedition.quotation) {
      return finalizeApiOptions.slice(0, 2);
    }

    const customQuotation = expedition.customQuotation;
    if (
      customQuotation?.type === CustomQuotationTypeEnum.OTHER &&
      customQuotation.otherDetail?.isMultipleExpedition
    ) {
      return finalizeApiOptions.filter((option) => option.value === "NONE_SIMPLIFIED");
    }

    return finalizeApiOptions;
  }, [expedition, finalizeApiOptions]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("finalizeModal.title")}
      description={t("finalizeModal.description")}
      size="md"
      contentClassName="max-w-[500px]"
      footer={
        <div className="flex w-full flex-col items-center gap-2">
          <Button disabled={!selected} className="w-full" onClick={onConfirm}>
            {t("common:actions.confirm")}
          </Button>
          <Button className="w-full" variant="outline" onClick={onClose}>
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      <RadioGroup
        name="finalizeExpedition"
        value={selected}
        onValueChange={(v) => setSelected(v as FinalizeApi)}
        aria-label={t("finalizeModal.methodLabel")}
      >
        {finalizeOptions.map(({ value, label, Icon }) => {
          const inputId = `${id}-${value}`;
          const isSelected = selected === value;
          return (
            <Label
              key={value}
              htmlFor={inputId}
              className={cn(
                "flex h-14 cursor-pointer items-center gap-2 rounded-lg border px-4",
                isSelected ? "border-primary bg-primary/5" : "border-gray-200",
              )}
            >
              <RadioGroupItem value={value} id={inputId} />
              <div className="flex flex-1 items-center justify-between gap-2">
                <span>{label}</span>
                {Icon && <Icon className="xs-screen:block hidden h-fit w-16" />}
              </div>
            </Label>
          );
        })}
      </RadioGroup>
    </Modal>
  );
};
